import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { WEBSERVICE } from "../utils/init";
import logo from "../assets/images/paybitologo.png";

// ─── Global styles ────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=DM+Mono:wght@400;500&display=swap');

  .pb-cart-root *, .pb-cart-root *::before, .pb-cart-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .pb-cart-root {
    font-family: 'Outfit', sans-serif;
    min-height: 100vh;
    background: #f0f2f5;
    color: #111;
  }

  /* ── Navbar ── */
  .pb-nav {
    background: #ffffff;
    border-bottom: 1px solid rgba(0,0,0,0.07);
    height: 68px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 40px;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 1px 20px rgba(0,0,0,0.06);
  }
  .pb-nav-logo { height: 34px; object-fit: contain; }
  .pb-nav-center {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    font-weight: 700;
    color: #111;
    letter-spacing: -0.2px;
  }
  .pb-nav-badge {
    background: #f7931a;
    color: #fff;
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 700;
    font-family: 'DM Mono', monospace;
    line-height: 1.6;
  }
  .pb-merchant-tag {
    display: flex;
    align-items: center;
    gap: 7px;
    background: #fff7ed;
    border: 1.5px solid #fed7aa;
    border-radius: 24px;
    padding: 6px 14px;
    font-size: 12.5px;
    font-weight: 600;
    color: #c2410c;
    letter-spacing: 0.1px;
  }

  /* ── Layout ── */
  .pb-container {
    max-width: 1040px;
    margin: 0 auto;
    padding: 40px 24px 80px;
  }
  .pb-page-title {
    font-size: 26px;
    font-weight: 800;
    color: #0f0f0f;
    letter-spacing: -0.6px;
    margin-bottom: 4px;
    line-height: 1.2;
  }
  .pb-page-sub {
    font-size: 13.5px;
    color: #9ca3af;
    font-weight: 400;
    margin-bottom: 28px;
  }
  .pb-layout {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 24px;
    align-items: start;
  }
  @media (max-width: 800px) {
    .pb-layout { grid-template-columns: 1fr; }
    .pb-nav { padding: 0 20px; }
    .pb-container { padding: 24px 16px 60px; }
  }

  /* ── Card ── */
  .pb-card {
    background: #ffffff;
    border-radius: 18px;
    border: 1px solid rgba(0,0,0,0.06);
    box-shadow: 0 4px 24px rgba(0,0,0,0.05);
    overflow: hidden;
  }

  /* ── Items header ── */
  .pb-items-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 26px;
    border-bottom: 1px solid #f3f4f6;
  }
  .pb-items-header-left { display: flex; align-items: center; gap: 9px; }
  .pb-items-title { font-size: 14.5px; font-weight: 700; color: #111; }
  .pb-items-count {
    background: #f3f4f6;
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 9px;
    border-radius: 20px;
    font-family: 'DM Mono', monospace;
  }
  .pb-updating-pill {
    font-size: 12px;
    color: #f7931a;
    display: flex;
    align-items: center;
    gap: 5px;
    font-weight: 500;
  }

  /* ── Product row ── */
  /* ── Stock badges ── */
  .pb-stock-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 5px;
    margin-top: 6px;
    font-family: 'DM Mono', monospace;
    letter-spacing: 0.2px;
  }
  .pb-stock-low {
    background: #fff7ed;
    color: #c2410c;
    border: 1px solid #fed7aa;
  }
  .pb-stock-out {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }
  .pb-stock-ok {
    background: #f0fdf4;
    color: #15803d;
    border: 1px solid #bbf7d0;
  }
  .pb-qty-limit-hint {
    font-size: 10.5px;
    color: #f97316;
    font-family: 'DM Mono', monospace;
    margin-top: 2px;
    text-align: right;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .pb-item-row {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    padding: 22px 26px;
    border-bottom: 1px solid #f9fafb;
    transition: background 0.15s;
    animation: fadeUp 0.3s ease forwards;
  }
  .pb-item-row:last-child { border-bottom: none; }
  .pb-item-row:hover { background: #fafafa; }

  .pb-item-img {
    width: 80px;
    height: 80px;
    border-radius: 14px;
    object-fit: cover;
    flex-shrink: 0;
    border: 1px solid #f0f0f0;
    background: #f3f4f6;
  }
  .pb-item-img-placeholder {
    width: 80px;
    height: 80px;
    border-radius: 14px;
    background: linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    border: 1px solid #e5e7eb;
  }

  .pb-item-info { flex: 1; min-width: 0; }
  .pb-item-name {
    font-size: 15px;
    font-weight: 700;
    color: #111;
    margin-bottom: 8px;
    letter-spacing: -0.2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .pb-attrs { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 10px; }
  .pb-attr-chip {
    font-size: 11.5px;
    font-weight: 500;
    background: #f0f9ff;
    color: #0369a1;
    border: 1px solid #bae6fd;
    border-radius: 6px;
    padding: 3px 9px;
  }
  .pb-sku {
    font-size: 11px;
    color: #d1d5db;
    font-family: 'DM Mono', monospace;
  }

  .pb-item-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 14px;
    flex-shrink: 0;
    min-width: 140px;
  }
  .pb-item-price {
    font-size: 17px;
    font-weight: 800;
    color: #0f0f0f;
    letter-spacing: -0.4px;
    font-family: 'DM Mono', monospace;
  }
  .pb-item-unit {
    font-size: 11.5px;
    color: #9ca3af;
    margin-top: 3px;
    font-family: 'DM Mono', monospace;
  }

  /* ── Qty control ── */
  .pb-qty-wrap {
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.2s;
  }
  .pb-qty-wrap:focus-within { border-color: #f7931a; box-shadow: 0 0 0 3px rgba(247,147,26,0.12); }
  .pb-qty-btn {
    width: 34px;
    height: 34px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-size: 17px;
    font-weight: 400;
    color: #374151;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
    line-height: 1;
    font-family: 'Outfit', sans-serif;
  }
  .pb-qty-btn:hover:not(:disabled) { background: rgba(247,147,26,0.1); color: #f7931a; }
  .pb-qty-btn:disabled { opacity: 0.35; cursor: not-allowed; }
  .pb-qty-input {
    width: 40px;
    height: 34px;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    font-family: 'DM Mono', monospace;
    color: #111;
    background: #ffffff;
    border: none;
    border-left: 1.5px solid #e5e7eb;
    border-right: 1.5px solid #e5e7eb;
    outline: none;
    -moz-appearance: textfield;
  }
  .pb-qty-input::-webkit-inner-spin-button,
  .pb-qty-input::-webkit-outer-spin-button { -webkit-appearance: none; }

  /* ── Remove btn ── */
  .pb-remove-btn {
    background: none !important;
    border: none !important;
    cursor: pointer;
    color: #d1d5db;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Outfit', sans-serif;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 !important;
    transition: color 0.15s;
    letter-spacing: 0.2px;
    box-shadow: none !important;
  }
  .pb-remove-btn:hover:not(:disabled) { color: #ef4444 !important; background: none !important; }
  .pb-remove-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── Summary card ── */
  .pb-summary-card {
    background: #ffffff;
    border-radius: 18px;
    border: 1px solid rgba(0,0,0,0.06);
    box-shadow: 0 4px 24px rgba(0,0,0,0.05);
    padding: 26px;
    position: sticky;
    top: 88px;
  }
  .pb-summary-title {
    font-size: 15px;
    font-weight: 800;
    color: #111;
    letter-spacing: -0.3px;
    margin-bottom: 20px;
  }
  .pb-summary-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    font-size: 13.5px;
    color: #6b7280;
  }
  .pb-summary-row-val {
    font-weight: 600;
    color: #374151;
    font-family: 'DM Mono', monospace;
    font-size: 13px;
  }
  .pb-summary-row-name {
    max-width: 175px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .pb-divider { height: 1px; background: #f3f4f6; margin: 14px 0; }
  .pb-fee-free { font-weight: 700 !important; color: #16a34a !important; font-family: 'Outfit', sans-serif !important; }

  .pb-total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0 4px;
  }
  .pb-total-label {
    font-size: 16px;
    font-weight: 800;
    color: #111;
    letter-spacing: -0.3px;
  }
  .pb-total-amount {
    font-size: 22px;
    font-weight: 800;
    color: #f7931a;
    letter-spacing: -0.8px;
    font-family: 'DM Mono', monospace;
  }

  /* ── Buttons ── */
  .pb-btn-checkout {
    width: 100%;
    padding: 15px !important;
    background: linear-gradient(135deg, #f7931a 0%, #f5730a 100%) !important;
    color: #ffffff !important;
    border: none !important;
    border-radius: 12px !important;
    font-size: 15px !important;
    font-weight: 700 !important;
    font-family: 'Outfit', sans-serif !important;
    cursor: pointer;
    margin-top: 20px;
    letter-spacing: 0.1px;
    transition: all 0.2s !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 4px 16px rgba(247,147,26,0.35) !important;
    text-shadow: none !important;
    outline: none;
  }
  .pb-btn-checkout:hover:not(:disabled) {
    background: linear-gradient(135deg, #e8830f 0%, #e06200 100%) !important;
    box-shadow: 0 6px 24px rgba(247,147,26,0.45) !important;
    transform: translateY(-1px);
    color: #fff !important;
  }
  .pb-btn-checkout:active:not(:disabled) { transform: translateY(0) !important; }
  .pb-btn-checkout:disabled { opacity: 0.6; cursor: not-allowed; transform: none !important; }

  .pb-btn-continue {
    width: 100%;
    padding: 13px !important;
    background: transparent !important;
    color: #6b7280 !important;
    border: 1.5px solid #e5e7eb !important;
    border-radius: 12px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    font-family: 'Outfit', sans-serif !important;
    cursor: pointer;
    margin-top: 10px;
    transition: all 0.2s !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-shadow: none !important;
    box-shadow: none !important;
    outline: none;
  }
  .pb-btn-continue:hover:not(:disabled) {
    border-color: #f7931a !important;
    color: #f7931a !important;
    background: #fff7ed !important;
  }
  .pb-btn-continue:disabled { opacity: 0.5; cursor: not-allowed; }

  .pb-btn-ghost {
    background: transparent !important;
    color: #f7931a !important;
    border: 2px solid #f7931a !important;
    border-radius: 10px !important;
    font-size: 14px !important;
    font-weight: 600 !important;
    font-family: 'Outfit', sans-serif !important;
    cursor: pointer;
    padding: 12px 28px !important;
    transition: all 0.2s !important;
    display: inline-flex !important;
    align-items: center;
    gap: 7px;
    box-shadow: none !important;
  }
  .pb-btn-ghost:hover { background: #fff7ed !important; }

  /* ── Misc ── */
  .pb-secure-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 16px;
    font-size: 11.5px;
    color: #d1d5db;
    font-weight: 500;
  }
  .pb-session-id {
    text-align: center;
    font-size: 10px;
    color: #e5e7eb;
    font-family: 'DM Mono', monospace;
    margin-top: 8px;
    letter-spacing: 0.4px;
  }

  /* ── Banners ── */
  .pb-banner {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 18px;
    border-radius: 12px;
    font-size: 13.5px;
    font-weight: 500;
    margin-bottom: 20px;
    line-height: 1.5;
  }
  .pb-banner-icon { flex-shrink: 0; margin-top: 1px; }
  .pb-banner-warn { background: #fffbeb; border: 1px solid #fde68a; color: #92400e; }
  .pb-banner-error { background: #fef2f2; border: 1px solid #fecaca; color: #991b1b; }
  .pb-banner-demo { background: #fffbeb; border: 1.5px dashed #fbbf24; color: #92400e; }

  /* ── Empty state ── */
  .pb-empty { padding: 80px 32px; text-align: center; }
  .pb-empty-icon {
    width: 72px; height: 72px;
    background: #f9fafb;
    border: 2px dashed #e5e7eb;
    border-radius: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 30px;
    margin: 0 auto 20px;
  }
  .pb-empty-title { font-size: 18px; font-weight: 800; color: #111; letter-spacing: -0.4px; margin-bottom: 8px; }
  .pb-empty-text { font-size: 13.5px; color: #9ca3af; line-height: 1.6; margin-bottom: 24px; }

  /* ── Loading ── */
  .pb-loading { padding: 72px 32px; display: flex; flex-direction: column; align-items: center; gap: 14px; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .pb-loader-ring {
    width: 44px; height: 44px;
    border: 3px solid #f3f4f6;
    border-top-color: #f7931a;
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
  }
  .pb-loading-text { font-size: 14px; color: #9ca3af; font-weight: 500; }
  .pb-spin { animation: spin 0.8s linear infinite; display: inline-block; }
`;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// ─── Cart Item Row ────────────────────────────────────────────────────────────
function CartItem({ item, onQtyChange, onRemove, updating }) {
  const [imgError, setImgError] = useState(false);
  const attrs =
    item.attributes_json
      ? typeof item.attributes_json === "string"
        ? JSON.parse(item.attributes_json)
        : item.attributes_json
      : {};
  const attrEntries = Object.entries(attrs).filter(([, v]) => v);
  const subtotal = (parseFloat(item.unit_price) * item.quantity).toFixed(2);

  const availQty = item.available_quantity != null ? parseInt(item.available_quantity) : null;
  const isOutOfStock = availQty !== null && availQty === 0;
  const isAtMax = availQty !== null && item.quantity >= availQty;
  const isLowStock = availQty !== null && availQty > 0 && availQty <= 3;

  const handleQtyInput = (e) => {
    const v = parseInt(e.target.value, 10);
    if (!isNaN(v) && v >= 1) {
      const capped = availQty !== null ? Math.min(v, availQty) : v;
      onQtyChange(item, capped);
    }
  };

  return (
    <div className="pb-item-row">
      {item.image_url && !imgError ? (
        <img src={item.image_url} alt={item.name} className="pb-item-img" onError={() => setImgError(true)} />
      ) : (
        <div className="pb-item-img-placeholder">🛍️</div>
      )}

      <div className="pb-item-info">
        <div className="pb-item-name" title={item.name}>{item.name}</div>
        {attrEntries.length > 0 && (
          <div className="pb-attrs">
            {attrEntries.map(([k, v]) => (
              <span key={k} className="pb-attr-chip">
                {k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, " ")}: {v}
              </span>
            ))}
          </div>
        )}
        <div className="pb-sku">SKU · {item.product_id}</div>

        {/* Stock badge */}
        {isOutOfStock ? (
          <div><span className="pb-stock-badge pb-stock-out">✕ Out of Stock</span></div>
        ) : isLowStock ? (
          <div><span className="pb-stock-badge pb-stock-low">⚠ Only {availQty} left</span></div>
        ) : availQty !== null ? (
          <div><span className="pb-stock-badge pb-stock-ok">✓ {availQty} in stock</span></div>
        ) : null}
      </div>

      <div className="pb-item-right">
        <div style={{ display: "flex", flexDirection: "column", gap: "3px", alignItems: "flex-end" }}>
          <div className="pb-item-price">${subtotal}</div>
          <div className="pb-item-unit">${parseFloat(item.unit_price).toFixed(2)} / unit</div>
        </div>

        <div>
          <div className="pb-qty-wrap" style={isOutOfStock ? { opacity: 0.4, pointerEvents: "none" } : {}}>
            <button className="pb-qty-btn" onClick={() => onQtyChange(item, item.quantity - 1)} disabled={updating || item.quantity <= 1}>−</button>
            <input
              className="pb-qty-input"
              type="number"
              min="1"
              max={availQty || undefined}
              value={item.quantity}
              onChange={handleQtyInput}
              disabled={updating || isOutOfStock}
            />
            <button className="pb-qty-btn" onClick={() => onQtyChange(item, item.quantity + 1)} disabled={updating || isAtMax || isOutOfStock}>+</button>
          </div>
          {isAtMax && !isOutOfStock && (
            <div className="pb-qty-limit-hint">Max qty reached</div>
          )}
        </div>

        <button className="pb-remove-btn" onClick={() => onRemove(item)} disabled={updating}>
          <svg viewBox="0 0 16 16" fill="currentColor" style={{ width: 12, height: 12 }}>
            <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66H14.5a.5.5 0 0 0 0-1h-.994A.287.287 0 0 0 13.5 2.5H11"/>
          </svg>
          Remove
        </button>
      </div>
    </div>
  );
}

// ─── Main Cart Page ───────────────────────────────────────────────────────────
const Cart = () => {
  const query = useQuery();
  const cartToken = query.get("token");
  const merchantParam = query.get("merchant");
  const isDemo = !cartToken;

  const DUMMY_CART = {
    cart_token: "demo-token-abc123",
    merchant_id: "M123",
    merchant_name: "TechStore Inc.",
    merchant_website_url: "https://example.com",
    status: "active",
    currency: "USD",
    total_amount: "219.96",
    items: [
      {
        product_id: "P001",
        name: "Wireless Noise-Cancelling Headphones",
        unit_price: "89.99",
        quantity: 1,
        available_quantity: 5,
        image_url: "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=120&h=120&fit=crop",
        attributes_json: { color: "Midnight Black", connectivity: "Bluetooth 5.0" },
      },
      {
        product_id: "P002",
        name: "Premium Cotton T-Shirt",
        unit_price: "29.99",
        quantity: 2,
        available_quantity: 2,
        image_url: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=120&h=120&fit=crop",
        attributes_json: { color: "Navy Blue", size: "M", material: "100% Cotton" },
      },
      {
        product_id: "P003",
        name: "Mechanical Gaming Keyboard",
        unit_price: "69.99",
        quantity: 1,
        available_quantity: 10,
        image_url: "https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=120&h=120&fit=crop",
        attributes_json: { color: "Space Gray", switch_type: "Cherry MX Red", layout: "TKL" },
      },
    ],
  };

  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!cartToken) {
      setTimeout(() => { setCart(DUMMY_CART); setLoading(false); }, 700);
      return;
    }
    try {
      setLoading(true); setError("");
      const res = await axios.get(`${WEBSERVICE}/cart/${cartToken}`);
      if (res.data?.status === "success") {
        setCart(res.data.data);
        if (res.data.data.status === "expired") setExpired(true);
      } else { setError(res.data?.message || "Failed to load cart."); }
    } catch (err) {
      if (err.response?.status === 410) setExpired(true);
      else setError("Unable to load cart. Please try again.");
    } finally { setLoading(false); }
  }, [cartToken]);

  useEffect(() => { fetchCart(); }, [fetchCart]);

  const recalcTotal = (items) =>
    items.reduce((s, i) => s + parseFloat(i.unit_price) * i.quantity, 0).toFixed(2);

  const handleQtyChange = async (item, newQty) => {
    if (newQty < 1) return;
    // Cap at available_quantity if set by merchant
    const availQty = item.available_quantity != null ? parseInt(item.available_quantity) : null;
    const cappedQty = availQty !== null ? Math.min(newQty, availQty) : newQty;
    if (cappedQty < 1) return;
    if (isDemo) {
      setCart((prev) => { const items = prev.items.map((i) => i.product_id === item.product_id ? { ...i, quantity: cappedQty } : i); return { ...prev, items, total_amount: recalcTotal(items) }; });
      return;
    }
    try {
      setUpdating(true);
      await axios.put(`${WEBSERVICE}/cart/update`, { cart_token: cartToken, product_id: item.product_id, quantity: cappedQty, attributes: item.attributes_json });
      setCart((prev) => { const items = prev.items.map((i) => i.product_id === item.product_id ? { ...i, quantity: cappedQty } : i); return { ...prev, items, total_amount: recalcTotal(items) }; });
    } catch { setError("Failed to update quantity."); }
    finally { setUpdating(false); }
  };

  const handleRemove = async (item) => {
    if (isDemo) {
      setCart((prev) => { const items = prev.items.filter((i) => i.product_id !== item.product_id); return { ...prev, items, total_amount: recalcTotal(items) }; });
      return;
    }
    try {
      setUpdating(true);
      await axios.delete(`${WEBSERVICE}/cart/remove`, { data: { cart_token: cartToken, product_id: item.product_id } });
      setCart((prev) => { const items = prev.items.filter((i) => i.product_id !== item.product_id); return { ...prev, items, total_amount: recalcTotal(items) }; });
    } catch { setError("Failed to remove item."); }
    finally { setUpdating(false); }
  };

  const handleCheckout = async () => {
    if (isDemo) { alert("Demo mode — connect a real cart session to checkout."); return; }
    try {
      setCheckingOut(true);
      const res = await axios.post(`${WEBSERVICE}/cart/checkout/init`, { cart_token: cartToken });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
      else { setError(res.data?.message || "Checkout failed."); setCheckingOut(false); }
    } catch { setError("Failed to initiate checkout."); setCheckingOut(false); }
  };

  const handleContinueShopping = () => {
    if (cart?.merchant_website_url) window.location.href = cart.merchant_website_url;
    else window.history.back();
  };

  const totalItems = cart?.items?.reduce((s, i) => s + i.quantity, 0) || 0;
  const totalAmount = parseFloat(cart?.total_amount || 0).toFixed(2);
  const currency = cart?.currency || "USD";
  const merchantName = cart?.merchant_name || merchantParam || "";

  return (
    <div className="pb-cart-root">
      <style>{GLOBAL_CSS}</style>

      {/* ── Navbar ── */}
      <nav className="pb-nav">
        <img src={logo} alt="PayBito" className="pb-nav-logo" onError={(e) => { e.target.style.display = "none"; }} />

        <div className="pb-nav-center">
          <svg viewBox="0 0 24 24" fill="none" stroke="#f7931a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 19, height: 19 }}>
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          Your Cart
          {totalItems > 0 && <span className="pb-nav-badge">{totalItems}</span>}
        </div>

        {merchantName ? (
          <div className="pb-merchant-tag">
            <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 13, height: 13 }}>
              <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3z"/>
            </svg>
            {merchantName}
          </div>
        ) : <div style={{ width: 120 }} />}
      </nav>

      <div className="pb-container">
        <div className="pb-page-title">Shopping Cart</div>
        <div className="pb-page-sub">Review your items and proceed to secure checkout</div>

        {/* Demo banner */}
        {isDemo && (
          <div className="pb-banner pb-banner-demo">
            <svg className="pb-banner-icon" viewBox="0 0 20 20" fill="currentColor" style={{ width: 15, height: 15 }}>
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
            </svg>
            <span>Demo mode — showing sample data. Pass a <strong style={{ fontFamily: "'DM Mono', monospace", background: "rgba(0,0,0,0.06)", padding: "1px 5px", borderRadius: 4 }}>?token=</strong> to load a real cart.</span>
          </div>
        )}

        {error && (
          <div className="pb-banner pb-banner-error">
            <svg className="pb-banner-icon" viewBox="0 0 20 20" fill="currentColor" style={{ width: 16, height: 16 }}>
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
            </svg>
            {error}
          </div>
        )}

        {expired && (
          <div className="pb-banner pb-banner-warn">
            <svg className="pb-banner-icon" viewBox="0 0 20 20" fill="currentColor" style={{ width: 16, height: 16 }}>
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z" clipRule="evenodd"/>
            </svg>
            Cart session expired. Please return to the store to start a new order.
          </div>
        )}

        {/* ── Main content ── */}
        {loading ? (
          <div className="pb-card">
            <div className="pb-loading">
              <div className="pb-loader-ring" />
              <div className="pb-loading-text">Loading your cart…</div>
            </div>
          </div>
        ) : expired ? (
          <div className="pb-card">
            <div className="pb-empty">
              <div className="pb-empty-icon">🕐</div>
              <div className="pb-empty-title">Session Expired</div>
              <div className="pb-empty-text">Your cart session has timed out.<br />Please return to the store and add items again.</div>
              <button className="pb-btn-ghost" onClick={handleContinueShopping}>← Return to Store</button>
            </div>
          </div>
        ) : cart?.items?.length === 0 ? (
          <div className="pb-card">
            <div className="pb-empty">
              <div className="pb-empty-icon">🛒</div>
              <div className="pb-empty-title">Cart is empty</div>
              <div className="pb-empty-text">You've removed all items.<br />Head back to continue shopping.</div>
              <button className="pb-btn-ghost" onClick={handleContinueShopping}>← Continue Shopping</button>
            </div>
          </div>
        ) : (
          <div className="pb-layout">

            {/* Left — Items */}
            <div className="pb-card">
              <div className="pb-items-header">
                <div className="pb-items-header-left">
                  <svg viewBox="0 0 20 20" fill="none" style={{ width: 16, height: 16 }}>
                    <path d="M3 4h14l-1.5 9H4.5L3 4z" stroke="#f7931a" strokeWidth="1.5" strokeLinejoin="round"/>
                    <circle cx="7.5" cy="16.5" r="1" fill="#f7931a"/>
                    <circle cx="13.5" cy="16.5" r="1" fill="#f7931a"/>
                  </svg>
                  <span className="pb-items-title">Items</span>
                  <span className="pb-items-count">{totalItems}</span>
                </div>
                {updating && (
                  <span className="pb-updating-pill">
                    <span className="pb-spin" style={{ display: "inline-block" }}>↻</span> Updating
                  </span>
                )}
              </div>

              {cart.items.map((item) => (
                <CartItem key={item.product_id} item={item} onQtyChange={handleQtyChange} onRemove={handleRemove} updating={updating} />
              ))}
            </div>

            {/* Right — Summary */}
            <div className="pb-summary-card">
              <div className="pb-summary-title">Order Summary</div>

              {cart.items.map((item) => (
                <div className="pb-summary-row" key={item.product_id}>
                  <span className="pb-summary-row-name">{item.name} ×{item.quantity}</span>
                  <span className="pb-summary-row-val">${(parseFloat(item.unit_price) * item.quantity).toFixed(2)}</span>
                </div>
              ))}

              <div className="pb-divider" />

              <div className="pb-summary-row">
                <span>Subtotal</span>
                <span className="pb-summary-row-val">{currency} {totalAmount}</span>
              </div>
              <div className="pb-summary-row">
                <span>Processing Fee</span>
                <span className="pb-fee-free">Free</span>
              </div>

              <div className="pb-divider" />

              <div className="pb-total-row">
                <span className="pb-total-label">Total</span>
                <span className="pb-total-amount">${totalAmount}</span>
              </div>

              <button className="pb-btn-checkout" onClick={handleCheckout} disabled={checkingOut || updating || cart?.items?.some(i => i.available_quantity === 0)}>
                {checkingOut ? (
                  <><span className="pb-spin">↻</span> Processing…</>
                ) : (
                  <>
                    <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 15, height: 15 }}>
                      <path fillRule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clipRule="evenodd"/>
                    </svg>
                    Proceed to Checkout
                  </>
                )}
              </button>

              <button className="pb-btn-continue" onClick={handleContinueShopping} disabled={checkingOut}>
                <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 14, height: 14 }}>
                  <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd"/>
                </svg>
                Continue Shopping
              </button>

              {/* Secure badge */}
              <div className="pb-secure-badge">
                <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 13, height: 13 }}>
                  <path fillRule="evenodd" d="M9.661 2.237a.531.531 0 01.678 0 11.947 11.947 0 007.078 2.749.5.5 0 01.479.425c.069.52.104 1.05.104 1.589 0 5.162-3.26 9.563-7.834 11.256a.48.48 0 01-.332 0C5.26 16.563 2 12.162 2 7c0-.538.035-1.069.104-1.589a.5.5 0 01.48-.425 11.947 11.947 0 007.077-2.749z" clipRule="evenodd"/>
                </svg>
                Secured by PayBito
              </div>

              {!isDemo && (
                <div className="pb-session-id">Session · {cartToken?.substring(0, 16)}…</div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;